// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/LauncherOrgToolbar.test.tsx
import {
  BookmarkSimple,
  Check,
  Funnel,
  Tag,
  User,
} from "@phosphor-icons/react";
import { Button, Checkbox, Flex, Popover, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  TOOLBAR_CONTROL_SIZE,
  TOOLBAR_ICON_SIZE,
} from "../../../components/toolbarControls";
import {
  type LauncherSavedView,
  type OrgLabel,
} from "../../../services/askPunttOrgFilters";
import {
  type LauncherSearch,
  type LauncherSort,
} from "../../../utils/askPunttLauncherSearch";

const OPTION_ROW_CLASS =
  "flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm text-puntt-neutral-gray-12 transition-colors hover:bg-puntt-neutral-gray-3";

const CLEAR_BUTTON_CLASS =
  "mt-2 w-full cursor-pointer rounded-md border-t border-puntt-neutral-gray-4 px-3 py-2 text-left text-sm text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3";

export const LAUNCHER_SORT_OPTIONS: {
  value: LauncherSort;
  labelKey: string;
  fallback: string;
}[] = [
  { value: "updatedAt", labelKey: "sortUpdatedAt", fallback: "Last updated" },
  { value: "createdAt", labelKey: "sortCreatedAt", fallback: "Date created" },
  { value: "name", labelKey: "sortName", fallback: "Name" },
];

function FilterClearButton({
  testId,
  label,
  onClear,
}: {
  testId: string;
  label: string;
  onClear: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      className={CLEAR_BUTTON_CLASS}
      onClick={onClear}
    >
      {label}
    </button>
  );
}

export function PeopleFilterList({
  testIdPrefix,
  selected,
  onToggle,
  onClear,
  peopleOptions,
}: {
  testIdPrefix: string;
  selected: string[];
  onToggle: (id: string, checked: boolean) => void;
  onClear: () => void;
  peopleOptions: { _id: string; name: string }[];
}) {
  const { t } = useTranslation("ui");
  const hasSelection = selected.length > 0;

  return (
    <>
      {peopleOptions.length === 0 ? (
        <Text size="2" className="px-2 py-1 text-puntt-neutral-gray-9">
          {t("noParticipantsYet")}
        </Text>
      ) : (
        <div className="flex flex-col gap-1">
          {peopleOptions.map((person) => {
            const checked = selected.includes(person._id);
            const optionTestId = `${testIdPrefix}-person-option-${person._id}`;
            return (
              <label
                key={person._id}
                htmlFor={optionTestId}
                data-testid={optionTestId}
                className={OPTION_ROW_CLASS}
              >
                <Checkbox
                  id={optionTestId}
                  checked={checked}
                  onCheckedChange={(value) =>
                    onToggle(person._id, value === true)
                  }
                  aria-label={person.name}
                />
                {person.name}
              </label>
            );
          })}
        </div>
      )}
      {hasSelection ? (
        <FilterClearButton
          testId={`${testIdPrefix}-people-clear`}
          label={t("clearFilters")}
          onClear={onClear}
        />
      ) : null}
    </>
  );
}

export function TagFilterList({
  testIdPrefix,
  options,
  selected,
  onToggle,
  onClear,
}: {
  testIdPrefix: string;
  options: OrgLabel[];
  selected: string[];
  onToggle: (slug: string, checked: boolean) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation("ui");
  const hasSelection = selected.length > 0;

  return (
    <>
      {options.length === 0 ? (
        <Text size="2" className="px-2 py-1 text-puntt-neutral-gray-9">
          {t("noOptionsAvailable", { defaultValue: "No options available" })}
        </Text>
      ) : (
        <div className="flex flex-col gap-1">
          {options.map((option) => {
            const checked = selected.some(
              (s) => s.toLowerCase() === option.slug.toLowerCase(),
            );
            const optionTestId = `${testIdPrefix}-tags-option-${option.slug}`;
            return (
              <label
                key={option._id}
                htmlFor={optionTestId}
                data-testid={optionTestId}
                className={OPTION_ROW_CLASS}
              >
                <Checkbox
                  id={optionTestId}
                  checked={checked}
                  onCheckedChange={(value) =>
                    onToggle(option.slug, value === true)
                  }
                  aria-label={option.name}
                />
                {option.name}
              </label>
            );
          })}
        </div>
      )}
      {hasSelection ? (
        <FilterClearButton
          testId={`${testIdPrefix}-tags-clear`}
          label={t("clearFilters")}
          onClear={onClear}
        />
      ) : null}
    </>
  );
}

export function PeopleFilter({
  testIdPrefix,
  selected,
  onToggle,
  onClear,
  peopleOptions,
}: {
  testIdPrefix: string;
  selected: string[];
  onToggle: (id: string, checked: boolean) => void;
  onClear: () => void;
  peopleOptions: { _id: string; name: string }[];
}) {
  const hasSelection = selected.length > 0;
  const buttonLabel = hasSelection ? `People (${selected.length})` : "People";

  return (
    <Popover.Root>
      <Popover.Trigger>
        <Button
          type="button"
          variant={hasSelection ? "soft" : "surface"}
          color={hasSelection ? "blue" : "gray"}
          size={TOOLBAR_CONTROL_SIZE}
          data-testid={`${testIdPrefix}-filter-people`}
          aria-label={
            hasSelection
              ? `Filter by people, ${selected.length} selected`
              : "Filter by people"
          }
          className="shrink-0 cursor-pointer"
        >
          <User size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
          <Text
            size="2"
            weight="regular"
            className={hasSelection ? undefined : "text-puntt-neutral-gray-11"}
          >
            {buttonLabel}
          </Text>
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        className="max-h-[min(70vh,420px)] min-w-[220px] overflow-y-auto p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <PeopleFilterList
          testIdPrefix={testIdPrefix}
          selected={selected}
          onToggle={onToggle}
          onClear={onClear}
          peopleOptions={peopleOptions}
        />
      </Popover.Content>
    </Popover.Root>
  );
}

export function TagFilter({
  testIdPrefix,
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  testIdPrefix: string;
  label: string;
  options: OrgLabel[];
  selected: string[];
  onToggle: (slug: string, checked: boolean) => void;
  onClear: () => void;
}) {
  const hasSelection = selected.length > 0;
  const buttonLabel = hasSelection ? `${label} (${selected.length})` : label;

  return (
    <Popover.Root>
      <Popover.Trigger>
        <Button
          type="button"
          variant={hasSelection ? "soft" : "surface"}
          color={hasSelection ? "blue" : "gray"}
          size={TOOLBAR_CONTROL_SIZE}
          data-testid={`${testIdPrefix}-filter-tags`}
          aria-label={
            hasSelection
              ? `Filter by ${label}, ${selected.length} selected`
              : `Filter by ${label}`
          }
          className="shrink-0 cursor-pointer"
        >
          <Tag size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
          <Text
            size="2"
            weight="regular"
            className={hasSelection ? undefined : "text-puntt-neutral-gray-11"}
          >
            {buttonLabel}
          </Text>
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        className="max-h-[min(70vh,420px)] min-w-[220px] overflow-y-auto p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <TagFilterList
          testIdPrefix={testIdPrefix}
          options={options}
          selected={selected}
          onToggle={onToggle}
          onClear={onClear}
        />
      </Popover.Content>
    </Popover.Root>
  );
}

function OverflowSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Text
        size="1"
        weight="medium"
        className="px-2 pt-1 text-puntt-neutral-gray-11"
      >
        {title}
      </Text>
      {children}
    </div>
  );
}

export function FiltersOverflowMenu({
  testIdPrefix,
  search,
  peopleOptions,
  tags,
  savedViews,
  activeSort,
  onTogglePeople,
  onClearPeople,
  onToggleTags,
  onClearTags,
  onSort,
  onApplyView,
  onRequestSaveView,
}: {
  testIdPrefix: string;
  search: LauncherSearch;
  peopleOptions: { _id: string; name: string }[];
  tags: OrgLabel[];
  savedViews: LauncherSavedView[];
  activeSort: LauncherSort;
  onTogglePeople: (id: string, checked: boolean) => void;
  onClearPeople: () => void;
  onToggleTags: (slug: string, checked: boolean) => void;
  onClearTags: () => void;
  onSort: (sort: LauncherSort) => void;
  onApplyView: (view: LauncherSavedView) => void;
  onRequestSaveView: () => void;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const selectedCount =
    (search.people?.length ?? 0) + (search.tags?.length ?? 0);
  const hasSelection = selectedCount > 0;
  const filtersWord = t("filters", { defaultValue: "Filters" });
  const buttonLabel = hasSelection
    ? `${filtersWord} (${selectedCount})`
    : filtersWord;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          type="button"
          variant={hasSelection ? "soft" : "surface"}
          color={hasSelection ? "blue" : "gray"}
          size={TOOLBAR_CONTROL_SIZE}
          data-testid={`${testIdPrefix}-filters`}
          aria-label={
            hasSelection
              ? `${filtersWord}, ${selectedCount} selected`
              : filtersWord
          }
          className="shrink-0 cursor-pointer"
        >
          <Funnel size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
          <Text
            size="2"
            weight="regular"
            className={hasSelection ? undefined : "text-puntt-neutral-gray-11"}
          >
            {buttonLabel}
          </Text>
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        data-testid={`${testIdPrefix}-filters-menu`}
        aria-label={filtersWord}
        className="max-h-[min(70vh,420px)] min-w-[220px] overflow-y-auto p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Flex direction="column" gap="3">
          <OverflowSection title="People">
            <PeopleFilterList
              testIdPrefix={testIdPrefix}
              selected={search.people ?? []}
              onToggle={onTogglePeople}
              onClear={onClearPeople}
              peopleOptions={peopleOptions}
            />
          </OverflowSection>
          <OverflowSection title={t("tags", { defaultValue: "Tags" })}>
            <TagFilterList
              testIdPrefix={testIdPrefix}
              options={tags}
              selected={search.tags ?? []}
              onToggle={onToggleTags}
              onClear={onClearTags}
            />
          </OverflowSection>
          <OverflowSection title={t("sort", { defaultValue: "Sort" })}>
            {LAUNCHER_SORT_OPTIONS.map((option) => {
              const isActive = option.value === activeSort;
              const label = t(option.labelKey, {
                defaultValue: option.fallback,
              });
              return (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`${testIdPrefix}-sort-${option.value}`}
                  aria-label={`Sort: ${option.fallback}`}
                  aria-pressed={isActive}
                  className={OPTION_ROW_CLASS}
                  onClick={() => {
                    onSort(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    size={14}
                    aria-hidden="true"
                    className={isActive ? "opacity-100" : "opacity-0"}
                  />
                  {label}
                </button>
              );
            })}
          </OverflowSection>
          <OverflowSection title={t("saveView", { defaultValue: "Save view" })}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-save-view-create`}
              className={OPTION_ROW_CLASS}
              onClick={() => {
                setOpen(false);
                onRequestSaveView();
              }}
            >
              {t("saveCurrentView", {
                defaultValue: "Save current filters…",
              })}
            </button>
            {savedViews.length === 0 ? (
              <Text size="2" className="px-2 py-1 text-puntt-neutral-gray-9">
                {t("noSavedViews", { defaultValue: "No saved views" })}
              </Text>
            ) : (
              savedViews.map((view) => (
                <button
                  key={view._id}
                  type="button"
                  data-testid={`${testIdPrefix}-saved-view-${view._id}`}
                  className={OPTION_ROW_CLASS}
                  onClick={() => {
                    onApplyView(view);
                    setOpen(false);
                  }}
                >
                  <BookmarkSimple size={14} aria-hidden="true" />
                  {view.name}
                  {view.isDefault ? " *" : ""}
                </button>
              ))
            )}
          </OverflowSection>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
